import { Button } from '@atlas/ui-primitives'
import { useState } from 'react'
import { ASSERTION_HINT, type Selection } from '@/components/classification/ChipSet'
import { ClassifyPicker } from '@/components/classification/ClassifyPicker'
import type { RailAxis } from '@/hooks/useLibrary'

export interface AddTupleFormProps {
	/** Registry axes + values offered by the picker. */
	axes: RailAxis[]
	/** Commit the assembled selection as a NEW tuple on the object. */
	onSubmit: (selection: Selection) => void
	onCancel: () => void
	/** True while the write is in flight. */
	isSaving?: boolean
	/** Set when the write failed, so the user is not left thinking it succeeded. */
	error?: string | null
}

/**
 * Assemble one NEW classification tuple — an independent assertion about the
 * object, not an addition to an existing one.
 *
 * A tuple carries no human-facing name (they are structural, numbered by
 * position), so this collects only the axis/value selection. It deliberately does
 * NOT offer "add to assertion 2": extending an existing tuple and asserting a new
 * fact are different operations, and conflating them is how the merged-tag
 * problem gets reintroduced by the UI.
 *
 * Presentational + storied: renders from props, never fetches.
 */
export function AddTupleForm({ axes, onSubmit, onCancel, isSaving = false, error = null }: AddTupleFormProps) {
	const [selection, setSelection] = useState<Selection>({})
	const isEmpty = Object.values(selection).every((values) => values.length === 0)

	return (
		<div className="space-y-3 rounded-ds-md border border-ds-border bg-ds-surface-subtle p-3">
			<p className="text-ds-2xs font-medium uppercase tracking-widest text-ds-text-subtle" title={ASSERTION_HINT}>
				New assertion
			</p>
			<p className="text-xs text-ds-text-subtle">
				A separate group of tags. Only tags added here count as going together.
			</p>
			<ClassifyPicker
				value={selection}
				axes={axes}
				onChange={setSelection}
				emptyLabel="Pick an axis and value to assert."
			/>
			{error ? <p className="text-sm text-ds-danger">{error}</p> : null}
			<div className="flex gap-2">
				<Button
					className="flex-1 bg-ds-text text-ds-background hover:opacity-90 disabled:opacity-50"
					isDisabled={isEmpty || isSaving}
					onPress={() => onSubmit(selection)}
				>
					{isSaving ? 'Saving…' : 'Add assertion'}
				</Button>
				<Button variant="secondary" isDisabled={isSaving} onPress={onCancel}>
					Cancel
				</Button>
			</div>
		</div>
	)
}
